import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, FileDown, Percent, Loader2, X, Plus, Trash2, Calculator, CheckSquare, Square } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from './supabase';
import { PickerSelect } from './ui/PickerSelect';
import { DataGrid } from './ui/DataGrid';
import { Thumb } from './ui/Thumb';
import { NumberCell, SaveState } from './ui/EditableCell';
import type { RowState } from './ui/useRowAutosave';
import { cn } from './ui/Table';
// Componentes de uiarc.dev (ver src/ui/arc)
import { Button } from './ui/arc/button';
import { Badge } from './ui/arc/badge';
import { MetricCard } from './ui/arc/metric-card';
import { ChipGroup } from './ui/arc/chip-group';
import SegmentedControl from './ui/arc/segmented-control';

// MÓDULO MAYORISTA — precios mayoristas (descuento sobre PVP de lista de TN),
// márgenes (con los costos de materiales ya cargados en Productos) y export de
// lista de precios PDF presentable a clientes mayoristas.
// El catálogo con fotos queda para una fase 2.
//
// UI: pantalla dentro de `.tw` (tokens + preflight acotado de uiarc, ver
// src/ui/arc/foundation.css), mismo patrón que src/Insumos.tsx.

interface TnProduct { id: number; name: string; category: string | null; active: boolean; has_free_shipping: boolean; image_url: string | null; canonical_url: string | null; direct_cost: number | null; direct_cost_currency: string | null; }
interface TnVariant { id: number; product_id: number; price: number | null; promotional_price: number | null; values: any; direct_cost?: number | null; direct_cost_currency?: string | null; }
interface WholesaleRow { product_id: number; included: boolean; override_pct: number | null; }
interface WSettings { default_discount_pct: number; list_footer: string | null; contact_line: string | null; }

const fmt = (n: number) => '$' + Math.round(n).toLocaleString('es-AR');

// ── Círculos de referencia de color, estimados por el NOMBRE del valor ──
// (vocabulario armado de los 150 valores reales del catálogo)
const RAINBOW = 'linear-gradient(135deg,#ff5f6d,#ffc371,#4ade80,#38bdf8,#a78bfa)';
const CHROME = 'linear-gradient(135deg,#ececec,#8f8f8f)';
const COLOR_RULES: [RegExp, string][] = [
  [/tornasol|arcoiris|\biris\b|^mix/i, RAINBOW],
  [/cromado|plateado|plata/i, CHROME],
  [/transparente|cristal/i, 'GLASS'],
  [/blanco c[aá]lido/i, '#ffdfa8'],
  [/blanco|opal/i, '#f7f5f2'],
  [/negro|fume/i, '#232323'],
  [/rojo|roja/i, '#d63031'],
  [/naranja|naraja/i, '#f2790f'],
  [/amarillo/i, '#f4c40e'],
  [/verde/i, '#2f9e44'],
  [/celeste/i, '#5bc4f2'],
  [/az[uú]l/i, '#2563eb'],
  [/violeta|lila|p[uú]rpura/i, '#8e44ad'],
  [/rosa|magenta|fucsia/i, '#e0357f'],
  [/marr[oó]n|madera/i, '#8d6748'],
  [/cobre|caramelo/i, '#b87333'],
  [/dorado|oro\b/i, '#d4af37'],
  [/gris/i, '#9aa0a6'],
];
interface Swatch { val: string; css: string; glass: boolean; translucent: boolean; }
function colorSwatch(val: string): Swatch | null {
  // combos "Azul y Cromado" → círculo mitad y mitad
  const parts = val.split(/\s+y\s+/i);
  const hexOf = (t: string): string | null => {
    for (const [re, css] of COLOR_RULES) if (re.test(t)) return css;
    return null;
  };
  if (parts.length === 2) {
    const a = hexOf(parts[0]); const b = hexOf(parts[1]);
    if (a && b && a !== 'GLASS' && b !== 'GLASS') {
      return { val, css: `linear-gradient(90deg, ${a} 50%, ${b} 50%)`, glass: false, translucent: false };
    }
  }
  const css = hexOf(val);
  if (!css) return null;
  return { val, css: css === 'GLASS' ? '#eef2f5' : css, glass: css === 'GLASS', translucent: /transl[uú]cid/i.test(val) };
}
function productSwatches(productName: string, valsPerVariant: string[][]): Swatch[] {
  const seen = new Map<string, Swatch>();
  // el nombre del producto también pinta (Kosmo Iris, Infinite Tornasolada, Verde Flúo)
  if (/tornasol|\biris\b|arcoiris/i.test(productName)) {
    seen.set(RAINBOW, { val: 'Tornasolado', css: RAINBOW, glass: false, translucent: false });
  }
  for (const vals of valsPerVariant) for (const v of vals) {
    const sw = colorSwatch(v);
    if (sw && !seen.has(sw.css + (sw.glass ? 'g' : '') + (sw.translucent ? 't' : ''))) {
      seen.set(sw.css + (sw.glass ? 'g' : '') + (sw.translucent ? 't' : ''), sw);
    }
  }
  return Array.from(seen.values()).slice(0, 10);
}

// Cálculo de costo compartido con la tabla (simples + acrílico por área con merma)
function calcMatsCost(items: any[], matById: Map<number, any>, st: any): number {
  if (!st) return 0;
  let total = 0;
  const REF_AREA = 1290 * 1860;
  const DENSITY = 11.8 / 4;
  for (const it of items) {
    const m = matById.get(Number(it.material_id));
    if (!m) continue;
    if (m.type === 'simple') {
      let u = m.unit_cost;
      if (m.currency === 'USD') u *= st.usd_rate;
      total += u * (Number(it.quantity) || 0);
    } else if (m.type === 'acrylic') {
      const th = it.thickness_mm || 4;
      let ppk = m.unit_cost;
      if (m.currency === 'USD') ppk *= st.usd_rate;
      const costPerSheet = ppk * (DENSITY * th) * (1 + st.iva_percent / 100);
      const area = (it.width_mm || 0) * (it.height_mm || 0) * (1 + st.acrylic_waste_percent / 100);
      total += (costPerSheet / REF_AREA) * area * (Number(it.quantity) || 0);
    }
  }
  return total;
}

// Popup de insumos desde la lista mayorista: edita los insumos BASE del producto
// y los específicos de cada variante, sin ir a la pantalla Productos.
// Es un modal (no edición en grilla) porque es un sub-formulario de N filas con su
// propio "Guardar": reemplaza la lista completa del producto/variante en una escritura.
function InsumosModal({ product, variants, matList, costCfg, onClose, onSaved, showSnackbar }: {
  product: TnProduct;
  variants: TnVariant[];
  matList: any[];
  costCfg: any;
  onClose: () => void;
  onSaved: () => void;
  showSnackbar: (msg: string, type: 'success' | 'error') => void;
}) {
  const [scope, setScope] = useState<'product' | number>('product'); // 'product' o variant_id
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const matById = useMemo(() => new Map(matList.map((m: any) => [Number(m.id), m])), [matList]);

  const valsOf = (v: TnVariant): string[] => {
    let raw: any = v.values;
    if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { raw = []; } }
    return Array.isArray(raw) ? raw.filter((x: any) => typeof x === 'string' && x.trim()) : [];
  };

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = scope === 'product'
      ? await supabase.from('tn_product_materials').select('*').eq('tn_product_id', product.id)
      : await supabase.from('tn_variant_materials').select('*').eq('tn_variant_id', scope);
    setRows((data || []).map((r: any) => ({ ...r })));
    setLoading(false);
  }, [scope, product.id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (scope === 'product') {
        await supabase.from('tn_product_materials').delete().eq('tn_product_id', product.id);
        if (rows.length) await supabase.from('tn_product_materials').insert(rows.map(r => ({
          tn_product_id: product.id, material_id: r.material_id, quantity: r.quantity,
          width_mm: r.width_mm || null, height_mm: r.height_mm || null, thickness_mm: r.thickness_mm || 4,
        })));
      } else {
        await supabase.from('tn_variant_materials').delete().eq('tn_variant_id', scope);
        if (rows.length) await supabase.from('tn_variant_materials').insert(rows.map(r => ({
          tn_variant_id: scope, material_id: r.material_id, quantity: r.quantity,
          width_mm: r.width_mm || null, height_mm: r.height_mm || null, thickness_mm: r.thickness_mm || 4,
        })));
      }
      showSnackbar('Insumos guardados', 'success');
      onSaved();
    } catch (e) {
      showSnackbar(`No se pudo guardar: ${String((e as Error).message || e)}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const upd = (i: number, patch: any) => setRows(rs => rs.map((r, ri) => ri === i ? { ...r, ...patch } : r));
  // Medidas y foco del `input` de uiarc (src/ui/arc/input.module.css)
  const inputCls = 'h-[var(--control-height-md)] rounded-[var(--radius-control)] border border-[var(--border-strong)] bg-card px-2 text-[length:var(--text-sm)] tabular-nums text-ink focus:border-[var(--foreground)] focus:outline-none';
  const cost = calcMatsCost(rows, matById, costCfg);

  return (
    // CLAUDE.md: overlay rgba(0,0,0,0.5) + contenido con var(--card) sólido.
    <div className="tw fixed inset-0 z-50 flex items-center justify-center bg-[rgba(0,0,0,0.5)] p-4" onClick={onClose}>
      <div className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-[var(--radius-surface)] border border-line bg-card p-5 text-ink shadow-[var(--shadow-floating)] animate-in fade-in-0 zoom-in-95"
        onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Insumos de ${product.name}`}>
        <div className="mb-1 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-[length:var(--text-base)] font-semibold text-ink">Insumos · {product.name}</h2>
            <p className="mt-0.5 text-[length:var(--text-sm)] text-faint">Los base aplican a todas las variantes; los de variante se suman a los base.</p>
          </div>
          <Button variant="ghost" size="sm" aria-label="Cerrar" onClick={onClose}><X size={16} /></Button>
        </div>

        {Number(product.direct_cost) > 0 && (
          <div className="mt-3 rounded-[var(--radius-control)] border border-line bg-muted px-3 py-2 text-[length:var(--text-sm)]">
            Reventa: costo directo <b className="tabular-nums">{product.direct_cost_currency === 'USD' ? `USD ${product.direct_cost}` : fmt(Number(product.direct_cost))}</b> — pisa los insumos. Se edita en <b>Productos → Insumos Base</b>.
          </div>
        )}

        <div className="mt-4">
          <SegmentedControl label="Qué insumos editar" value={String(scope)}
            onValueChange={v => setScope(v === 'product' ? 'product' : Number(v))}
            options={[{ value: 'product', label: 'Base (todo el producto)' },
              ...variants.map(v => ({ value: String(v.id), label: valsOf(v).join(' · ') || 'estándar' }))]} />
        </div>

        <div className="mt-4">
          {loading ? (
            <div className="flex items-center gap-2 py-6 text-[length:var(--text-sm)] text-faint"><Loader2 size={15} className="spin" /> Cargando…</div>
          ) : (
            <>
              {rows.length === 0 && (
                <p className="py-3 text-[length:var(--text-sm)] text-faint">Sin insumos cargados {scope === 'product' ? 'en la base' : 'para esta variante'}.</p>
              )}
              {rows.map((r, i) => {
                const m = matById.get(Number(r.material_id));
                return (
                  <div key={i} className="mb-2 flex flex-wrap items-center gap-2">
                    <PickerSelect
                      className="min-w-[200px] flex-1"
                      placeholder="Material…"
                      value={r.material_id || null}
                      onChange={(v) => upd(i, { material_id: v == null ? 0 : Number(v) })}
                      options={matList.map((mm: any) => ({ value: mm.id, label: mm.name, image: mm.image_url || null }))}
                    />
                    <input type="number" min={0} step="any" value={r.quantity ?? 1} onChange={e => upd(i, { quantity: Number(e.target.value) })}
                      className={cn(inputCls, 'w-16 text-center')} title="Cantidad" aria-label="Cantidad" />
                    {m?.type === 'acrylic' && (
                      <>
                        <input type="number" min={0} value={r.width_mm ?? ''} placeholder="ancho" onChange={e => upd(i, { width_mm: Number(e.target.value) })} className={cn(inputCls, 'w-[72px] text-center')} title="Ancho (mm)" />
                        <span className="text-faint">×</span>
                        <input type="number" min={0} value={r.height_mm ?? ''} placeholder="alto" onChange={e => upd(i, { height_mm: Number(e.target.value) })} className={cn(inputCls, 'w-[72px] text-center')} title="Alto (mm)" />
                        <input type="number" min={0} value={r.thickness_mm ?? 4} onChange={e => upd(i, { thickness_mm: Number(e.target.value) })} className={cn(inputCls, 'w-14 text-center')} title="Espesor (mm)" />
                      </>
                    )}
                    <Button variant="ghost" size="sm" aria-label="Quitar insumo" title="Quitar"
                      onClick={() => setRows(rs => rs.filter((_, ri) => ri !== i))}><Trash2 size={14} /></Button>
                  </div>
                );
              })}
              <Button variant="secondary" size="sm" onClick={() => setRows(rs => [...rs, { material_id: '', quantity: 1, thickness_mm: 4 }])}>
                <Plus size={14} /> Agregar insumo
              </Button>

              <div className="mt-4 flex items-center justify-between rounded-[var(--radius-control)] border border-line bg-muted px-3 py-2">
                <span className="text-[length:var(--text-sm)] text-soft">Costo estimado {scope === 'product' ? '(base)' : '(esta variante, sin los base)'}</span>
                <b className="tabular-nums">{fmt(cost)}</b>
              </div>

              <div className="mt-4 flex justify-end gap-2 border-t border-line pt-4">
                <Button variant="secondary" onClick={onClose}>Cerrar</Button>
                <Button loading={saving} onClick={save}>Guardar insumos</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

type Flag = 'en_lista' | 'fuera' | 'margen_bajo' | 'sin_costo';

export default function Wholesale({ showSnackbar }: { showSnackbar: (msg: string, type: 'success' | 'error') => void }) {
  const [products, setProducts] = useState<TnProduct[]>([]);
  const [variants, setVariants] = useState<TnVariant[]>([]);
  const [rows, setRows] = useState<Map<number, WholesaleRow>>(new Map());
  const [settings, setSettings] = useState<WSettings>({ default_discount_pct: 40, list_footer: null, contact_line: null });
  const [costs, setCosts] = useState<Map<number, number>>(new Map()); // variant_id -> costo materiales
  const [matList, setMatList] = useState<any[]>([]);          // cost_materials (para el editor)
  const [costCfg, setCostCfg] = useState<any>(null);          // usd_rate / iva / merma
  const [matsFor, setMatsFor] = useState<TnProduct | null>(null); // popup de insumos
  const [loading, setLoading] = useState(true);
  const [savingPct, setSavingPct] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  // Filtros de la grilla (el texto lo maneja el buscador del DataGrid)
  const [fCat, setFCat] = useState<string | null>(null);
  const [fFlag, setFFlag] = useState<Flag | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [prods, vars, ws, wp, cs, mats, pMats, vMats] = await Promise.all([
      supabase.from('tn_products').select('id, name, category, active, has_free_shipping, image_url, canonical_url, direct_cost, direct_cost_currency').eq('active', true).order('name'),
      supabase.from('tn_variants').select('id, product_id, price, promotional_price, values, direct_cost, direct_cost_currency'),
      supabase.from('wholesale_settings').select('*').eq('id', 1).maybeSingle(),
      supabase.from('wholesale_products').select('*'),
      supabase.from('cost_settings').select('usd_rate, eur_rate, iva_percent, acrylic_waste_percent').eq('id', 1).maybeSingle(),
      supabase.from('cost_materials').select('id, name, type, unit_cost, currency, image_url').order('name'),
      supabase.from('tn_product_materials').select('*'),
      supabase.from('tn_variant_materials').select('*'),
    ]);
    setProducts((prods.data as TnProduct[]) || []);
    setVariants((vars.data as TnVariant[]) || []);
    if (ws.data) setSettings(ws.data as WSettings);
    setRows(new Map(((wp.data as WholesaleRow[]) || []).map(r => [r.product_id, r])));

    // Costo por variante — misma lógica que Productos (materiales simples + acrílico por área)
    const st = cs.data as any;
    setCostCfg(st);
    setMatList((mats.data as any[]) || []);
    const matById = new Map(((mats.data as any[]) || []).map(m => [Number(m.id), m]));
    const pByProduct = new Map<number, any[]>();
    for (const pm of ((pMats.data as any[]) || [])) {
      const arr = pByProduct.get(pm.tn_product_id) || [];
      arr.push(pm); pByProduct.set(pm.tn_product_id, arr);
    }
    const vByVariant = new Map<number, any[]>();
    for (const vm of ((vMats.data as any[]) || [])) {
      const arr = vByVariant.get(vm.tn_variant_id) || [];
      arr.push(vm); vByVariant.set(vm.tn_variant_id, arr);
    }
    const cmap = new Map<number, number>();
    for (const v of ((vars.data as TnVariant[]) || [])) {
      const items = [...(vByVariant.get(v.id) || []), ...(pByProduct.get(v.product_id) || [])];
      if (items.length) cmap.set(v.id, calcMatsCost(items, matById, st));
    }
    setCosts(cmap);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const rowFor = (pid: number): WholesaleRow => rows.get(pid) || { product_id: pid, included: true, override_pct: null };
  const pctFor = (pid: number): number => {
    const r = rowFor(pid);
    return r.override_pct != null ? Number(r.override_pct) : Number(settings.default_discount_pct) || 0;
  };
  const priceOf = (v: TnVariant): number => Number(v.promotional_price || v.price || 0);
  const wholesalePrice = (v: TnVariant): number => priceOf(v) * (1 - pctFor(v.product_id) / 100);

  // Margen mayorista: cobro por transferencia (sin IVA — no se factura) → 3% Clean
  // Managers por el cobro, menos costo del producto.
  const marginOf = (v: TnVariant, p: TnProduct): { m: number; pct: number; pctCost: number; cost: number } | null => {
    // costo directo (reventa) pisa materiales — prioridad: variante > producto.
    // En USD convierte con el usd_rate de la config.
    const dcOf = (obj: any): number => {
      const val = Number(obj?.direct_cost) || 0;
      if (val <= 0) return 0;
      const cur = obj?.direct_cost_currency;
      return cur === 'USD' ? val * (costCfg?.usd_rate || 0)
        : cur === 'EUR' ? val * (costCfg?.eur_rate || 1800)
        : val;
    };
    const direct = dcOf(v) || dcOf(p);
    const cost = direct > 0 ? direct : costs.get(v.id);
    if (cost == null || cost <= 0) return null;
    const pm = wholesalePrice(v);
    if (pm <= 0) return null;
    const totalCost = cost + (p.has_free_shipping ? 7000 : 0);
    const m = pm - pm * 0.03 - totalCost;
    return { m, pct: (m / pm) * 100, pctCost: (m / totalCost) * 100, cost: totalCost };
  };

  async function saveSettings(patch: Partial<WSettings>) {
    setSavingPct(true);
    const next = { ...settings, ...patch };
    setSettings(next);
    const { error } = await supabase.from('wholesale_settings')
      .update({ ...patch, updated_at: new Date().toISOString() }).eq('id', 1);
    setSavingPct(false);
    if (error) showSnackbar('No se pudo guardar la configuración', 'error');
  }

  async function saveRow(pid: number, patch: Partial<WholesaleRow>) {
    const next = { ...rowFor(pid), ...patch };
    setRows(prev => new Map(prev).set(pid, next));
    const { error } = await supabase.from('wholesale_products')
      .upsert({ ...next, updated_at: new Date().toISOString() });
    if (error) showSnackbar('No se pudo guardar', 'error');
  }

  // % propio por producto con AUTOGUARDADO (mismo criterio que useRowAutosave): pinta al
  // instante y escribe 700ms después de la última tecla. No se usa el hook porque la tabla
  // es un upsert por product_id, no un update por id.
  const pctTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const [pctState, setPctState] = useState<Record<string, RowState>>({});
  function editPct(pid: number, raw: string) {
    const override_pct = raw === '' ? null : Number(raw);
    const next = { ...rowFor(pid), override_pct };
    setRows(prev => new Map(prev).set(pid, next));
    clearTimeout(pctTimers.current[pid]);
    pctTimers.current[pid] = setTimeout(async () => {
      setPctState(s => ({ ...s, [pid]: 'saving' }));
      const { error } = await supabase.from('wholesale_products').upsert({ ...next, updated_at: new Date().toISOString() });
      setPctState(s => ({ ...s, [pid]: error ? 'error' : 'saved' }));
      if (error) { showSnackbar('No se pudo guardar el %', 'error'); return; }
      setTimeout(() => setPctState(s => { const n = { ...s }; delete n[pid]; return n; }), 1600);
    }, 700);
  }

  // Catálogo agrupado por categoría (lo usan la grilla, el PDF y el HTML).
  const grouped = useMemo(() => {
    const byCat = new Map<string, { product: TnProduct; vars: { v: TnVariant; label: string }[]; swatches: Swatch[] }[]>();
    for (const p of products) {
      const vs = variants.filter(v => v.product_id === p.id && priceOf(v) > 0);
      if (!vs.length) continue;
      // Variantes agrupadas por PRECIO. La etiqueta de cada grupo son los valores
      // comunes a todas sus variantes: el color (que no cambia el precio) desaparece
      // y queda solo lo que lo explica (ej: "Chica" / "Grande").
      // values viene de la DB como STRING JSON de strings planos: '["50 cm","Verde"]'
      // (el sync lo guarda así; la API de TN en vivo usa otro formato con {es:...}).
      const valsOf = (v: TnVariant): string[] => {
        let raw: any = v.values;
        if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { raw = []; } }
        if (!Array.isArray(raw)) return [];
        return raw
          .map((x: any) => typeof x === 'string' ? x : String(x?.es ?? Object.values(x || {})[0] ?? ''))
          .map((t: string) => t.trim()).filter(Boolean);
      };
      const byPrice = new Map<number, TnVariant[]>();
      for (const v of vs) {
        const arr2 = byPrice.get(priceOf(v)) || [];
        arr2.push(v); byPrice.set(priceOf(v), arr2);
      }
      const vars = Array.from(byPrice.values()).map((group) => {
        const common = valsOf(group[0]).filter(val => group.every(g => valsOf(g).includes(val)));
        let label = common.join(' · ');
        if (!label) {
          // sin valor común (tamaños distintos que casualmente comparten precio):
          // listamos el primer atributo de cada variante (suele ser el tamaño)
          const firsts = Array.from(new Set(group.map(g => valsOf(g)[0]).filter(Boolean)));
          label = firsts.slice(0, 4).join(' / ');
        }
        return { v: group[0], label };
      }).sort((a, b) => priceOf(a.v) - priceOf(b.v));
      const nm = p.name.toLowerCase();
      let cat = (p.category || 'Otros').trim() || 'Otros';
      if (/kosmo/.test(nm)) cat = 'Línea Kosmo';
      else if (/infinite/.test(nm) && !/reloj/.test(nm)) cat = 'Línea Infinite';
      const arr = byCat.get(cat) || [];
      arr.push({ product: p, vars, swatches: productSwatches(p.name, vs.map(valsOf)) });
      byCat.set(cat, arr);
    }
    return Array.from(byCat.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, variants]);

  // Filas de la grilla: una por variante-precio, en orden categoría → producto → precio.
  type GridRow = { id: number; cat: string; product: TnProduct; v: TnVariant; label: string; vi: number; nVars: number; swatches: Swatch[] };
  const allRows: GridRow[] = useMemo(() => grouped.flatMap(([cat, items]) =>
    items.flatMap(({ product, vars, swatches }) =>
      vars.map(({ v, label }, vi) => ({ id: v.id, cat, product, v, label, vi, nVars: vars.length, swatches })))),
  [grouped]);

  const isFlag = (r: GridRow, f: Flag) => {
    const inc = rowFor(r.product.id).included;
    if (f === 'en_lista') return inc;
    if (f === 'fuera') return !inc;
    const mg = marginOf(r.v, r.product);
    return f === 'sin_costo' ? !mg : !!mg && mg.pctCost < 30;
  };
  const gridRows = useMemo(
    () => allRows.filter(r => (!fCat || r.cat === fCat) && (!fFlag || isFlag(r, fFlag))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allRows, fCat, fFlag, rows, costs, settings, costCfg],
  );

  // Resumen: productos en lista + variantes con problemas (solo de los que están en lista).
  const stats = useMemo(() => {
    const totalProducts = grouped.reduce((s, [, items]) => s + items.length, 0);
    let included = 0, low = 0, noCost = 0, lowIn = 0, noCostIn = 0;
    for (const [, items] of grouped) for (const i of items) if (rowFor(i.product.id).included) included++;
    for (const r of allRows) {
      const mg = marginOf(r.v, r.product);
      const inc = rowFor(r.product.id).included;
      if (!mg) { noCost++; if (inc) noCostIn++; } else if (mg.pctCost < 30) { low++; if (inc) lowIn++; }
    }
    return { totalProducts, included, low, noCost, lowIn, noCostIn };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped, allRows, rows, costs, settings, costCfg]);
  const includedCount = stats.included;

  async function setAllIncluded(value: boolean) {
    // Actúa sobre los productos que pasan los filtros de categoría y chips.
    const pids = Array.from(new Set(gridRows.map(r => r.product.id)));
    if (!pids.length) return;
    setRows(prev => {
      const next = new Map(prev);
      for (const pid of pids) next.set(pid, { ...(next.get(pid) || { product_id: pid, included: true, override_pct: null }), included: value });
      return next;
    });
    const payload = pids.map(pid => ({ ...(rows.get(pid) || { product_id: pid, override_pct: null }), product_id: pid, included: value, updated_at: new Date().toISOString() }));
    const { error } = await supabase.from('wholesale_products').upsert(payload);
    if (error) showSnackbar('No se pudo guardar la selección', 'error');
    else showSnackbar(`${pids.length} producto(s) ${value ? 'agregados a' : 'sacados de'} la lista`, 'success');
  }

  // ===== PDF: lista de precios presentable (sin márgenes ni costos, obvio) =====
  async function downloadPdf() {
    setPdfBusy(true);
    try {
      const BRAND: [number, number, number] = [148, 30, 33];
      const INK: [number, number, number] = [17, 17, 17];
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const W = doc.internal.pageSize.getWidth();

      // Logo (si está disponible en /logo.png)
      let logoData: string | null = null;
      try {
        const blob = await fetch('/logo.png').then(r => (r.ok ? r.blob() : null));
        if (blob) logoData = await new Promise<string>((res) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result)); fr.readAsDataURL(blob); });
      } catch { /* sin logo */ }

      const header = () => {
        doc.setFillColor(...BRAND);
        doc.rect(0, 0, W, 3, 'F');
        if (logoData) {
          try { doc.addImage(logoData, 'PNG', 14, 9, 30, 0); } catch { /* logo inválido */ }
        } else {
          doc.setTextColor(...BRAND); doc.setFont('helvetica', 'bold'); doc.setFontSize(20);
          doc.text('krilic', 14, 18);
        }
        doc.setTextColor(120, 120, 120); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
        doc.text('DECO QUE ILUMINA', W - 14, 13, { align: 'right' });
        doc.setTextColor(...INK); doc.setFont('helvetica', 'bold'); doc.setFontSize(14);
        doc.text('Lista de precios mayorista', W - 14, 19, { align: 'right' });
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(110, 110, 110);
        doc.text('Lista ' + new Date().toLocaleDateString('es-AR', { month: 'long', year: 'numeric' }), W - 14, 24, { align: 'right' });
      };

      const body: any[] = [];
      for (const [cat, items] of grouped) {
        const inc = items.filter(i => rowFor(i.product.id).included);
        if (!inc.length) continue;
        body.push([{ content: cat.toUpperCase(), colSpan: 3, styles: { fillColor: [246, 241, 241], textColor: BRAND, fontStyle: 'bold', fontSize: 8.5, cellPadding: { top: 3, bottom: 2.5, left: 3 } } }]);
        for (const { product, vars } of inc) {
          vars.forEach(({ v, label }, vi) => {
            // título solo en la primera línea; las siguientes son solo la variante indentada
            const cell = vars.length === 1 ? product.name
              : vi === 0 ? { content: `${product.name}\n    ${label || 'base'}`, styles: { fontStyle: 'bold' as const } }
              : { content: `    ${label || 'variante'}`, styles: { textColor: [110, 110, 110] as [number, number, number] } };
            body.push([cell, { content: fmt(priceOf(v)), styles: { textColor: [150, 150, 150] } }, { content: fmt(wholesalePrice(v)), styles: { fontStyle: 'bold', textColor: INK } }]);
          });
        }
      }

      autoTable(doc, {
        startY: 46,
        head: [['Producto', 'PVP sugerido', 'Precio mayorista']],
        body,
        margin: { top: 30, left: 14, right: 14, bottom: 22 },
        styles: { font: 'helvetica', fontSize: 9, cellPadding: { top: 2.2, bottom: 2.2, left: 3, right: 3 }, lineColor: [235, 232, 232], lineWidth: 0.1 },
        headStyles: { fillColor: BRAND, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
        columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 34, halign: 'right' }, 2: { cellWidth: 38, halign: 'right' } },
        alternateRowStyles: { fillColor: [252, 251, 251] },
        didDrawPage: () => {
          header();
          if (doc.getNumberOfPages() === 1) {
            doc.setFontSize(8); doc.setTextColor(60, 60, 60);
            doc.setFont('helvetica', 'bold');
            doc.text('Precios expresados en pesos argentinos. No incluyen IVA.', 14, 33);
            doc.setFont('helvetica', 'normal');
            doc.text([settings.list_footer || '', settings.contact_line || ''].filter(Boolean).join('  ·  '), 14, 38);
          }
          const H = doc.internal.pageSize.getHeight();
          doc.setDrawColor(220, 216, 216); doc.line(14, H - 18, W - 14, H - 18);
          doc.setFontSize(7.5); doc.setTextColor(120, 120, 120); doc.setFont('helvetica', 'normal');
          doc.text('Precios en pesos argentinos, sin IVA. ' + (settings.contact_line || ''), 14, H - 6);
          doc.text(`Página ${doc.getNumberOfPages()}`, W - 14, H - 6, { align: 'right' });
        },
      });

      doc.save(`krilic-lista-mayorista-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      showSnackbar(`Error generando el PDF: ${String((e as Error).message || e)}`, 'error');
    } finally {
      setPdfBusy(false);
    }
  }

  // Lista de precios en HTML: archivo autocontenido con fotos (para mandar por mail/WhatsApp;
  // las imágenes cargan del CDN de Tiendanube). Mismo criterio que el PDF: solo tildados,
  // agrupado por categoría, sin márgenes ni costos.
  function downloadHtml() {
    const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const hoy = 'Lista ' + new Date().toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
    let rowsHtml = '';
    for (const [cat, items] of grouped) {
      const inc = items.filter(i => rowFor(i.product.id).included);
      if (!inc.length) continue;
      rowsHtml += `<h2>${esc(cat)}</h2><div class="grid">`;
      for (const { product, vars, swatches } of inc) {
        const dots = swatches.map(sw =>
          `<span class="dot" tabindex="0" data-c="${esc(sw.val)}" style="background:${sw.css};${sw.glass ? 'border:1px dashed #9aa0a6;' : ''}${sw.translucent ? 'opacity:.55;' : ''}"></span>`).join('');
        const url = product.canonical_url;
        const wrap = (inner: string) => url ? `<a href="${esc(url)}" target="_blank" rel="noopener">${inner}</a>` : inner;
        const img = wrap(product.image_url
          ? `<img src="${esc(product.image_url)}" alt="" loading="lazy">`
          : '<div class="noimg">Krilic</div>');
        const lines = vars.map(({ v, label }) => `
          <div class="line"><span class="vlabel">${vars.length > 1 ? esc(label || 'variante') : '&nbsp;'}</span>
          <span class="mayor">${fmt(wholesalePrice(v))}</span></div>`).join('');
        rowsHtml += `<div class="item">${img}<div class="info"><div class="pname">${wrap(esc(product.name))}</div>${dots ? `<div class="dots">${dots}</div>` : ''}${lines}</div></div>`;
      }
      rowsHtml += '</div>';
    }
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Krilic — Lista de precios mayorista</title>
<style>
  :root{--brand:#941e21;--ink:#1c1917;--muted:#8a8480;--line:#e8e2dc;}
  *{box-sizing:border-box}
  body{margin:0;font-family:'Poppins',system-ui,-apple-system,sans-serif;color:var(--ink);background:#faf8f6;}
  .page{max-width:860px;margin:0 auto;padding:0 24px;}
  .top{border-top:6px solid var(--brand);padding:28px 0 18px;display:flex;align-items:baseline;justify-content:space-between;flex-wrap:wrap;gap:8px;}
  .logo{height:54px;width:auto;display:block;}
  .tag{font-size:.7rem;letter-spacing:.18em;text-transform:uppercase;color:var(--muted);}
  h1{font-size:1.5rem;margin:.4em 0 .1em;font-weight:700;}
  .fecha{margin:0 0 8px;color:var(--muted);font-size:.85rem;}
  h2{font-size:.8rem;letter-spacing:.12em;text-transform:uppercase;color:var(--brand);margin:26px 0 8px;border-bottom:2px solid var(--brand);display:table;padding-bottom:2px;}
  .grid{margin:0;}
  .item{background:#fff;border-bottom:1px solid var(--line);padding:10px 6px;display:flex;gap:14px;align-items:flex-start;}
  .item:first-child{border-top:1px solid var(--line);}
  .item img{width:56px;height:56px;object-fit:cover;border-radius:8px;background:#eee;flex-shrink:0;}
  .noimg{width:56px;height:56px;border-radius:8px;background:#f3ece9;color:var(--brand);display:flex;align-items:center;justify-content:center;font-size:.62rem;font-weight:700;flex-shrink:0;}
  .info{flex:1;min-width:0;}
  .pname{font-weight:700;font-size:.92rem;line-height:1.25;margin-bottom:4px;}
  .pname a{color:var(--ink);text-decoration:none;}
  .pname a:hover{color:var(--brand);text-decoration:underline;}
  .item > a{flex-shrink:0;display:block;}
  .dots{display:flex;gap:5px;margin:2px 0 6px;flex-wrap:wrap;}
  .dot{width:16px;height:16px;border-radius:50%;display:inline-block;border:1px solid rgba(0,0,0,.2);position:relative;cursor:pointer;outline:none;}
  .dot::after{content:attr(data-c);position:absolute;bottom:calc(100% + 6px);left:50%;transform:translateX(-50%);background:#221d1a;color:#fff;font-size:.68rem;font-weight:600;padding:3px 8px;border-radius:6px;white-space:nowrap;opacity:0;pointer-events:none;transition:opacity .12s;z-index:3;}
  .dot:hover::after,.dot:focus::after{opacity:1;}
  .line{display:flex;align-items:baseline;gap:10px;font-size:.85rem;padding:1px 0;max-width:560px;}
  .vlabel{flex:1;color:var(--muted);font-size:.8rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .mayor{font-weight:800;min-width:92px;text-align:right;font-variant-numeric:tabular-nums;}
  .terms{border:1px solid var(--line);border-left:4px solid var(--brand);border-radius:8px;background:#fff;padding:10px 14px;font-size:.82rem;line-height:1.6;margin:4px 0 10px;}
  .terms a{color:var(--brand);font-weight:600;text-decoration:none;}
  @media print{.item{break-inside:avoid}body{background:#fff}}
</style></head><body>
<div class="page">
<div class="top"><img class="logo" src="https://acdn-us.mitiendanube.com/stores/004/010/714/themes/common/logo-224413002-1761002759-34f548a9bc21e02e7eb428f3c369a9d41761002759-320-0.webp" alt="Krilic"><div class="tag">Deco que ilumina</div></div>
<h1>Lista de precios mayorista</h1>
<p class="fecha">${hoy}</p>
<div class="terms"><b>Precios expresados en pesos argentinos. No incluyen IVA.</b><br>${esc(settings.list_footer || 'Precios sujetos a modificación sin previo aviso.')}${settings.contact_line ? '<br><a href="mailto:' + esc(settings.contact_line) + '">' + esc(settings.contact_line) + '</a>' : ''}</div>
${rowsHtml}
</div>
</body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `krilic-lista-mayorista-${new Date().toISOString().slice(0, 10)}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const categories = grouped.map(([cat, items]) => ({ value: cat, label: cat, sublabel: `${items.length} producto(s)` }));
  const pct = Number(settings.default_discount_pct) || 0;
  // Campo de texto con las medidas del `input` de uiarc (para los que guardan al salir del campo).
  const fieldCls = 'h-[var(--control-height-md)] w-full rounded-[var(--radius-control)] border border-[var(--border-strong)] bg-card px-3 text-[length:var(--text-sm)] text-ink placeholder:text-faint transition-colors hover:border-[var(--foreground)] focus:border-[var(--foreground)] focus:outline-none';

  return (
    <div className="tw w-full min-w-0 pb-12 pt-1">
      {/* ── Encabezado ─────────────────────────────────────────────────────── */}
      <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[length:var(--text-2xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">Mayorista</h1>
          <p className="mt-1 max-w-[72ch] text-[length:var(--text-sm)] text-soft">
            Precio mayorista como % de descuento sobre el PVP de Tiendanube, con el margen de cada variante. Lo que cambies se guarda solo.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" disabled={!includedCount} onClick={downloadHtml}><Download size={16} /> Lista HTML con fotos</Button>
          <Button loading={pdfBusy} disabled={!includedCount} onClick={downloadPdf}>
            <FileDown size={16} /> Lista PDF <Badge size="sm" className="ml-0.5">{includedCount}</Badge>
          </Button>
        </div>
      </header>

      {/* ── Resumen ─────────────────────────────────────────────────────────── */}
      <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard label="En la lista" value={stats.included} locale="es-AR"
          context={`de ${stats.totalProducts} productos con precio`} />
        <MetricCard label="Descuento global" value={pct} suffix="%" locale="es-AR"
          context="Sobre el PVP, para los productos sin % propio" />
        <MetricCard label="Margen bajo" value={stats.lowIn} locale="es-AR"
          context="Variantes en lista con menos de 30% de ganancia sobre costo" />
        <MetricCard label="Sin costo cargado" value={stats.noCostIn} locale="es-AR"
          context="Variantes en lista sin insumos: no se ve su margen" />
      </section>

      {/* ── Configuración de la lista ───────────────────────────────────────
          Se guarda al salir del campo (no en cada tecla): son 3 campos de una sola fila. */}
      <section className="mb-5 rounded-[var(--radius-surface)] border border-line bg-card p-5 shadow-xs">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-[length:var(--text-base)] font-semibold text-ink">Configuración de la lista</h2>
            <p className="mt-0.5 text-[length:var(--text-sm)] text-faint">El descuento aplica a todo el catálogo; el pie y el contacto salen en el PDF y el HTML.</p>
          </div>
          <span className="shrink-0 text-[length:var(--text-xs)] text-faint">{savingPct ? 'guardando…' : ''}</span>
        </div>
        <div className="grid gap-4 md:grid-cols-[11rem_1fr_1fr]">
          <label className="grid min-w-0">
            <span className="mb-2 text-[length:var(--text-sm)] font-medium text-ink">Descuento global</span>
            <span className="relative flex items-center">
              <input type="number" min={0} max={90} value={settings.default_discount_pct}
                onChange={e => setSettings(s => ({ ...s, default_discount_pct: Number(e.target.value) }))}
                onBlur={e => saveSettings({ default_discount_pct: Number(e.target.value) })}
                className={cn(fieldCls, 'pr-8 tabular-nums')} />
              <Percent size={14} className="pointer-events-none absolute right-3 text-faint" />
            </span>
          </label>
          <label className="grid min-w-0">
            <span className="mb-2 text-[length:var(--text-sm)] font-medium text-ink">Pie de la lista (condiciones)</span>
            <input value={settings.list_footer || ''} placeholder="Ej: Compra mínima $300.000. Pagos por transferencia. Entrega en 7-10 días hábiles."
              onChange={e => setSettings(s => ({ ...s, list_footer: e.target.value }))}
              onBlur={e => saveSettings({ list_footer: e.target.value })}
              className={fieldCls} />
          </label>
          <label className="grid min-w-0">
            <span className="mb-2 text-[length:var(--text-sm)] font-medium text-ink">Contacto</span>
            <input value={settings.contact_line || ''} placeholder="Ej: mayorista@krilic.com.ar · +54 9 11 3688-7992"
              onChange={e => setSettings(s => ({ ...s, contact_line: e.target.value }))}
              onBlur={e => saveSettings({ contact_line: e.target.value })}
              className={fieldCls} />
          </label>
        </div>
      </section>

      {/* ── Grilla ──────────────────────────────────────────────────────────
          Una fila por variante-precio (las variantes que solo cambian el color van juntas).
          Orden por defecto: categoría → producto → precio; las columnas numéricas ordenan
          para encontrar rápido lo que deja poco margen. */}
      <DataGrid
        rows={gridRows}
        loading={loading}
        rowKey={r => r.id}
        // Fuera de la lista: fila atenuada entera, salvo el checkbox (que es como se vuelve a incluir).
        rowClassName={r => !rowFor(r.product.id).included && '[&>td:not(:first-child)]:opacity-50'}
        pageSize={60}
        searchPlaceholder="Buscar producto o categoría…"
        emptyTitle="Sin productos con precio"
        emptyText="Sincronizá los productos de Tiendanube desde el Catálogo."
        filtersActive={fCat != null || fFlag != null}
        onClearFilters={() => { setFCat(null); setFFlag(null); }}
        footer={<>Margen = precio mayorista − 3% de cobro por transferencia − costo (insumos o costo directo, + $7.000 si tiene envío gratis). Sin IVA: la venta mayorista no se factura.</>}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <PickerSelect className="w-56" placeholder="Todas las categorías" clearable
              value={fCat} onChange={v => setFCat(v == null ? null : String(v))} options={categories} />
            <ChipGroup label="Filtros rápidos" multiple={false}
              value={fFlag ? [fFlag] : []}
              onValueChange={v => setFFlag((v[0] as Flag) ?? null)}
              options={[
                { value: 'en_lista', label: `En lista · ${stats.included}` },
                { value: 'fuera', label: `Fuera · ${stats.totalProducts - stats.included}` },
                { value: 'margen_bajo', label: `Margen bajo · ${stats.low}` },
                { value: 'sin_costo', label: `Sin costo · ${stats.noCost}` },
              ]} />
            <Button variant="ghost" size="sm" title="Agrega a la lista los productos que pasan los filtros" onClick={() => setAllIncluded(true)}><CheckSquare size={14} /> Tildar</Button>
            <Button variant="ghost" size="sm" title="Saca de la lista los productos que pasan los filtros" onClick={() => setAllIncluded(false)}><Square size={14} /> Destildar</Button>
          </div>
        }
        cols={[
          { key: 'lista', header: 'Lista', width: 'w-14', align: 'center',
            render: r => r.vi === 0 ? (
              <input type="checkbox" checked={rowFor(r.product.id).included} aria-label={`Incluir ${r.product.name} en la lista`}
                onChange={e => saveRow(r.product.id, { included: e.target.checked })}
                className="h-4 w-4 cursor-pointer accent-[var(--foreground)]" />
            ) : null },
          { key: 'producto', header: 'Producto', sort: r => r.product.name,
            render: r => {
              const inc = rowFor(r.product.id).included;
              return (
                <div className="flex min-w-[16rem] items-center gap-3">
                  {r.vi === 0
                    ? <Thumb src={r.product.image_url} name={r.product.name} size="sm" />
                    : <span className="w-7 shrink-0 text-center text-faint">↳</span>}
                  <div className="min-w-0">
                    {r.vi === 0 ? (
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-ink" title={r.product.name}>{r.product.name}</span>
                        {r.nVars > 1 && <span className="shrink-0 text-[length:var(--text-xs)] text-faint">{r.label || 'base'}</span>}
                      </div>
                    ) : (
                      <span className="truncate text-soft" title={r.product.name}>{r.label || 'variante'}</span>
                    )}
                    {r.vi === 0 && r.swatches.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {r.swatches.map((sw, si) => (
                          // color/forma salen del dato (nombre del valor): estilo inline inevitable
                          <span key={si} title={sw.val}
                            className={cn('inline-block h-3 w-3 rounded-full border', sw.glass ? 'border-dashed border-[#9aa0a6]' : 'border-[rgba(0,0,0,0.2)]', sw.translucent && 'opacity-50')}
                            style={{ background: sw.css }} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            } },
          { key: 'cat', header: 'Categoría', hideOnMobile: true, sort: r => r.cat,
            render: r => <span className="block max-w-[11rem] truncate text-[length:var(--text-sm)] text-soft" title={r.cat}>{r.cat}</span> },
          { key: 'pvp', header: 'PVP', align: 'right', sort: r => priceOf(r.v),
            render: r => <span className="text-soft">{fmt(priceOf(r.v))}</span> },
          { key: 'pct', header: '% desc', align: 'right', width: 'w-32', sort: r => pctFor(r.product.id),
            render: r => r.vi === 0 ? (
              <div className="flex items-center justify-end gap-1">
                <SaveState state={pctState[String(r.product.id)]} />
                <span className="w-20">
                  <NumberCell value={rowFor(r.product.id).override_pct ?? ''} placeholder={String(pct)}
                    className={cn(rowFor(r.product.id).override_pct == null && 'text-faint')}
                    onChange={v => editPct(r.product.id, v)} />
                </span>
                <span className="text-faint">%</span>
              </div>
            ) : <span className="pr-4 text-[length:var(--text-xs)] text-faint">{pctFor(r.product.id)}%</span> },
          { key: 'mayor', header: 'Mayorista', align: 'right', sort: r => wholesalePrice(r.v),
            render: r => <span className="font-semibold text-ink">{fmt(wholesalePrice(r.v))}</span> },
          { key: 'costo', header: 'Costo', align: 'right', hideOnMobile: true, sort: r => marginOf(r.v, r.product)?.cost ?? null,
            render: r => {
              const mg = marginOf(r.v, r.product);
              return mg ? <span className="text-soft" title="Costo (+envío bonificado si aplica)">{fmt(mg.cost)}</span> : <span className="text-faint">—</span>;
            } },
          { key: 'margen', header: 'Margen', align: 'right', sort: r => marginOf(r.v, r.product)?.pctCost ?? null,
            render: r => {
              const mg = marginOf(r.v, r.product);
              if (!mg) return r.vi > 0 ? <span className="text-faint">—</span> : (
                <Button variant="ghost" size="sm" onClick={() => setMatsFor(r.product)} title="Cargar los insumos para ver el margen">
                  <Calculator size={14} /> Cargar costo
                </Button>
              );
              // Semáforo por ganancia sobre costo: <30% rojo · 30-50% amarillo · >50% verde
              const tone = mg.pctCost < 30 ? 'danger' : mg.pctCost <= 50 ? 'warning' : 'success';
              return (
                <div className="flex items-center justify-end gap-2" title={`${mg.pct.toFixed(0)}% del precio mayorista`}>
                  <span className="text-ink">{fmt(mg.m)}</span>
                  <Badge size="sm" tone={tone}>{`${mg.pctCost >= 0 ? '+' : ''}${mg.pctCost.toFixed(0)}% s/costo`}</Badge>
                </div>
              );
            } },
          { key: 'acc', header: '', width: 'w-12', align: 'right',
            render: r => r.vi === 0 ? (
              <Button variant="ghost" size="sm" aria-label={`Insumos de ${r.product.name}`} title="Ver y editar insumos del producto"
                className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
                onClick={() => setMatsFor(r.product)}><Calculator size={14} /></Button>
            ) : null },
        ]}
      />

      {matsFor && (
        <InsumosModal
          product={matsFor}
          variants={variants.filter(v => v.product_id === matsFor.id)}
          matList={matList}
          costCfg={costCfg}
          showSnackbar={showSnackbar}
          onClose={() => setMatsFor(null)}
          onSaved={() => { setMatsFor(null); load(); }}
        />
      )}
    </div>
  );
}
